import React, { useCallback, useEffect, useState } from 'react';

import { LoadingView } from '../../components/common/loader';
import { SuccessMessage } from '../../components/common/AllConfirmationMessages';
import { PatientDetails } from '../../components/common/patientDetails';
import { PickedPatientsRxData } from '../../components/common/pickedPatientsRxData';
import { ErrorMessages } from '../../components/error/errorMessages';
import { RouteControlSheet } from '../../components/common/routeControlSheet';
import { TAB } from '../../components/common/routeControlSheet/constants';

import Axios from '../../networking/interceptor';
import { driver_get_patients } from '../../networking/urlEndPoints';

import { fetchDriverActiveRoutes } from '../../services/apis/dispense/routeAudit';
import { fetchHandedOverMissed, fetchMissedVerifications, openBase64Pdf, saveMissedVerification } from '../../services/apis/dispense/missedVerification';
import { useAuthSelector } from '../../services/selectors/authSelector';

import * as Common from '../../components/common';
import * as Icons from '../../assets/svg/icons';
import * as Hooks from '../../hooks';
import * as routeName from '../../routes/routeName';

// Safety cap so a bad paging response can never loop forever.
const MAX_PAGES = 25;

const fetchAllPages = async (url, params) => {
	const rows = [];
	let page = 1;
	let lastPage = 1;
	do {
		const response = await Axios.get(url, { params: { ...params, page } });
		const data = response?.data;
		rows.push(...(Array.isArray(data?.data) ? data.data : []));
		lastPage = Number(data?.meta?.last_page) || 1;
		page += 1;
	} while (page <= lastPage && page <= MAX_PAGES);
	return rows;
};

const uniqueRx = (list) => list.filter((rx, i) => list.findIndex((other) => String(other?.id) === String(rx?.id)) === i);

// One card per patient with the medications from each picked route.
const buildPatientList = (routes, pendingByRoute) => {
	const byId = new Map();
	routes.forEach((route, routeIndex) => {
		(pendingByRoute[routeIndex] || []).forEach((patient) => {
			if (!patient?.id) return;
			const entry = byId.get(patient.id) || { ...patient, route: patient.route ?? route, rxList: [] };
			entry.rxList.push(...(patient.patients_win_rx_data || []));
			byId.set(patient.id, entry);
		});
	});
	return Array.from(byId.values()).map((entry) => ({ ...entry, rxList: uniqueRx(entry.rxList) }));
};

const isDoseDelivered = (dose) => String(dose?.delivery_status || '').toLowerCase() === 'delivered';

const rxDosesOf = (rx) => (rx?.investigate_dilaudid_delivery_history || rx?.investigate_dilaudid_delivery_all || []).filter(Boolean);

// Inside each medication card: the driver's "Mark as Missed" tick and comment.
// Both are saved straight away; the delivery status is never changed. A
// multi-dose RX also asks which dose(s) were missed.
const VerificationRow = ({ rx, verification, saving, onSave }) => {
	const savedComment = verification?.comment || '';
	const savedDoseKey = (verification?.frequency_dose_ids || []).join(',');
	const doses = rxDosesOf(rx);
	const isMultiDose = doses.length > 1;
	// Delivered doses can't be missed, so they are not offered. When only one
	// dose is left it is chosen for the driver.
	const openDoses = doses.filter((dose) => !isDoseDelivered(dose));
	const onlyOpenDoseId = openDoses.length === 1 ? openDoses[0].id : null;
	const defaultDoseIds = useCallback(
		() => (savedDoseKey ? savedDoseKey.split(',').map(Number) : (onlyOpenDoseId ? [onlyOpenDoseId] : [])),
		[savedDoseKey, onlyOpenDoseId]
	);
	const [comment, setComment] = useState(savedComment);
	const [doseIds, setDoseIds] = useState(defaultDoseIds);
	const [doseError, setDoseError] = useState('');
	const isVerified = Boolean(verification?.is_verified_missed);
	const commentChanged = comment.trim() !== savedComment.trim();

	useEffect(() => {
		setComment(savedComment);
	}, [savedComment]);

	useEffect(() => {
		setDoseIds(defaultDoseIds());
	}, [defaultDoseIds]);

	const handleVerifiedChange = (checked) => {
		if (checked && isMultiDose && doseIds.length === 0) {
			setDoseError('Choose which dose was missed first.');
			return;
		}
		setDoseError('');
		onSave(rx, checked, comment, checked ? doseIds : []);
	};

	const handleDoseToggle = (dose) => {
		if (saving || isDoseDelivered(dose)) return;
		const next = doseIds.includes(dose.id) ? doseIds.filter((id) => id !== dose.id) : [...doseIds, dose.id];
		setDoseIds(next);
		setDoseError('');
		// Already verified: save the new dose choice straight away; no dose left untick it.
		if (isVerified) onSave(rx, next.length > 0, comment, next);
	};

	return (
		<div className="mt-2 border-t border-slate-200 pt-2">
			{/* Same tick box as the medication selection in the route list. */}
			<label className={`flex w-fit items-center gap-2 whitespace-nowrap text-sm font-semibold text-slate-800 ${saving ? 'opacity-60' : 'cursor-pointer'}`}>
				<span
					className={`flex items-center justify-center shrink-0 w-7 h-7 rounded border transition-all duration-200 shadow-sm ${isVerified ? 'border-red-600 bg-red-600 hover:bg-red-700 text-white' : 'border-gray-300 bg-white hover:border-red-400 hover:bg-red-50/50'}`}
				>
					<input
						type="checkbox"
						className="sr-only"
						checked={isVerified}
						disabled={saving}
						onChange={(event) => handleVerifiedChange(event.target.checked)}
					/>
					{isVerified && (
						<svg className="w-3 h-3 pointer-events-none" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
							<path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
						</svg>
					)}
				</span>
				Mark as Missed
				{isVerified && verification?.verified_by_name ? (
					<span className="text-xs font-normal text-slate-500">by {verification.verified_by_name}</span>
				) : null}
			</label>

			{isMultiDose && openDoses.length > 0 ? (
				<div className="mt-2">
					<p className="mb-1 text-xs font-semibold text-slate-600">Which dose was missed?</p>
					<div className="flex flex-wrap gap-1.5">
						{openDoses.map((dose, index) => {
							const chosen = doseIds.includes(dose.id);
							return (
								<button
									key={dose.id}
									type="button"
									disabled={saving}
									onClick={() => handleDoseToggle(dose)}
									className={`inline-flex items-center gap-1 rounded border px-2 py-1 text-[12px] font-medium ${chosen
										? 'border-red-600 bg-red-600 text-white'
										: 'border-gray-300 bg-white text-gray-700 hover:border-red-400'}`}
								>
									Dose {dose?.dose_no ?? index + 1}{dose?.dose ? ` · ${dose.dose}` : ''}
								</button>
							);
						})}
					</div>
					{doseError ? <p className="mt-1 text-xs font-medium text-red-600">{doseError}</p> : null}
				</div>
			) : null}

			<div className="mt-2 flex items-start gap-2">
				<textarea
					rows={1}
					value={comment}
					onChange={(event) => setComment(event.target.value)}
					placeholder="Note for pharmacist"
					className="min-h-[38px] min-w-0 flex-1 resize-y rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-slate-400 focus:outline-none"
				/>
				{/* Fixed width so "Saving…" doesn't squeeze the comment box. */}
				<button
					type="button"
					disabled={saving || !commentChanged}
					onClick={() => onSave(rx, isVerified, comment, isVerified ? doseIds : [])}
					className="h-[38px] w-[68px] shrink-0 whitespace-nowrap rounded-md bg-slate-900 text-xs font-semibold text-white disabled:opacity-40"
				>
					{saving ? 'Saving…' : 'Save'}
				</button>
			</div>
		</div>
	);
};

const PickedPatients = () => {
	const { pharmacyId } = useAuthSelector();
	const navigate = Hooks.useCustomNavigate();

	const [routes, setRoutes] = useState([]);
	const [patients, setPatients] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');
	const [verifications, setVerifications] = useState({});
	const [verificationError, setVerificationError] = useState('');
	const [savingRxIds, setSavingRxIds] = useState([]);
	const [successMessage, setSuccessMessage] = useState('');
	const [searchText, setSearchText] = useState('');
	const [view, setView] = useState('full');
	const [showHandoverSheet, setShowHandoverSheet] = useState(false);
	// Verified-missed medicines already handed over (accepted) today: only
	// these go in the downloadable list.
	const [handedOverCount, setHandedOverCount] = useState(0);
	const [downloading, setDownloading] = useState(false);

	const loadPatients = useCallback(async () => {
		if (!pharmacyId) return;
		setLoading(true);
		setError('');
		setVerificationError('');
		try {
			const activeRoutes = (await fetchDriverActiveRoutes(pharmacyId))
				.map((route) => `${route?.route ?? ''}`.trim())
				.filter(Boolean);
			const pendingByRoute = await Promise.all(
				activeRoutes.map((route) => fetchAllPages(driver_get_patients, { route, pharmacy_id: pharmacyId }))
			);
			const patientList = buildPatientList(activeRoutes, pendingByRoute);
			setRoutes(activeRoutes);
			setPatients(patientList);

			const winrxDataIds = patientList.flatMap((patient) => patient.rxList.map((rx) => rx?.id)).filter(Boolean);
			if (winrxDataIds.length > 0) {
				try {
					const rows = await fetchMissedVerifications({ pharmacyId, winrxDataIds });
					setVerifications(Object.fromEntries(rows.map((row) => [String(row.winrx_data_id), row])));
				} catch (_) {
					setVerifications({});
					setVerificationError('Could not load saved verifications. Please refresh.');
				}
			} else {
				setVerifications({});
			}
		} catch (_) {
			setError('Oops! Got an issue while fetching Data.Please try again!');
		} finally {
			setLoading(false);
		}
	}, [pharmacyId]);

	useEffect(() => {
		loadPatients();
	}, [loadPatients]);

	const loadHandedOverCount = useCallback(async () => {
		if (!pharmacyId) return;
		try {
			const data = await fetchHandedOverMissed({ pharmacyId });
			setHandedOverCount(Number(data?.count) || 0);
		} catch (_) {
			setHandedOverCount(0);
		}
	}, [pharmacyId]);

	useEffect(() => {
		loadHandedOverCount();
	}, [loadHandedOverCount]);

	const handleDownloadList = async () => {
		if (downloading) return;
		if (handedOverCount === 0) {
			setVerificationError('Hand over is mandatory to download the list. Please hand over the missed medication first.');
			return;
		}
		setDownloading(true);
		setVerificationError('');
		// The link shows a loader until the PDF is ready; only then it opens.
		try {
			const data = await fetchHandedOverMissed({ pharmacyId, asPdf: true });
			if (data?.encoded_pdf) {
				openBase64Pdf(data.encoded_pdf, data?.file_name || 'verify-missed.pdf');
			} else {
				setVerificationError('Could not create the list. Please try again.');
			}
		} catch (requestError) {
			setVerificationError(requestError?.response?.data?.message || 'Could not download the list. Please try again.');
		} finally {
			setDownloading(false);
		}
	};

	useEffect(() => {
		if (!successMessage) return undefined;
		const timer = setTimeout(() => setSuccessMessage(''), 3000);
		return () => clearTimeout(timer);
	}, [successMessage]);

	const handleSaveVerification = async (rx, isVerifiedMissed, comment, frequencyDoseIds = []) => {
		const key = String(rx?.id);
		if (!rx?.id || !pharmacyId || savingRxIds.includes(key)) return;
		setSavingRxIds((current) => [...current, key]);
		setVerificationError('');
		try {
			const saved = await saveMissedVerification({
				pharmacyId,
				winrxDataId: rx.id,
				isVerifiedMissed,
				comment: comment.trim(),
				frequencyDoseIds,
			});
			setVerifications((current) => {
				const next = { ...current };
				if (saved?.is_verified_missed || saved?.comment) next[key] = saved;
				else delete next[key];
				return next;
			});
			setSuccessMessage('Saved.');
		} catch (requestError) {
			setVerificationError(requestError?.response?.data?.message || 'Could not save. Please try again.');
		} finally {
			setSavingRxIds((current) => current.filter((id) => id !== key));
		}
	};


	// Tabs sort every medicine on the picked routes, ticked or not: one dose a
	// day goes under Not Delivered, more than one under Partially Delivered. The
	// driver ticks them there.
	const doseTypeOf = (rx) => (rxDosesOf(rx).length > 1 ? 'partial' : 'full');
	const rxMatchesView = (rx) => doseTypeOf(rx) === view;
	// All medicines verified as missed, on every tab: these must be handed over.
	const verifiedMissedTotal = patients
		.flatMap((patient) => patient.rxList)
		.filter((rx) => verifications[String(rx?.id)]?.is_verified_missed).length;
	const missedCountFor = (type) => patients
		.flatMap((patient) => patient.rxList)
		.filter((rx) => doseTypeOf(rx) === type).length;
	const viewPatients = patients
		.map((patient) => ({ ...patient, rxList: patient.rxList.filter(rxMatchesView) }))
		.filter((patient) => patient.rxList.length > 0);

	// Filters this page only, by name, PHN, phone or address.
	const searchTerm = searchText.trim().toLowerCase();
	const visiblePatients = !searchTerm ? viewPatients : viewPatients.filter((patient) => [
		patient?.first_name,
		patient?.last_name,
		`${patient?.first_name ?? ''} ${patient?.last_name ?? ''}`,
		`${patient?.last_name ?? ''} ${patient?.first_name ?? ''}`,
		patient?.preferred_name,
		patient?.phn,
		patient?.phone,
		patient?.full_address,
	].some((value) => `${value ?? ''}`.toLowerCase().includes(searchTerm)));

	return (
		<div className="app-wrapper flex justify-center">
			<div className="app-screen no-shades w-full xl:w-3/12 lg:w-4/12 md:w-5/12 sm:w-6/12 h-full overflow-x-hidden">
				<div className="relative p-4">
					<Common.TopBar />

					<div className="title-row my-2 flex items-center justify-between gap-2">
						<h1 className="text-xl font-bold">Missed Medication</h1>
						<button
							type="button"
							onClick={() => {
								loadPatients();
								loadHandedOverCount();
							}}
							disabled={loading}
							className="inline-flex h-9 items-center justify-center rounded-md border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 disabled:opacity-50"
						>
							Refresh
						</button>
					</div>

					{/* Home button beside the search, same as the shared SearchBox on other screens. */}
					<div className="mb-3 flex items-center gap-3">
						<button
							type="button"
							onClick={() => navigate(routeName.DASHBOARD_PHARMACY)}
							className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-slate-300 bg-slate-50 text-slate-700 transition hover:bg-slate-100"
							aria-label="Go to homepage"
						>
							<Icons.HomeIcon />
						</button>
						{/* Always shown, also while the list loads; it filters once patients are in. */}
						<input
							type="search"
							value={searchText}
							onChange={(event) => setSearchText(event.target.value)}
							placeholder="Search patient by name, PHN or phone"
							className="h-12 min-w-0 flex-1 rounded-lg border border-slate-300 bg-slate-50 px-4 text-sm text-slate-800 placeholder:text-slate-400 focus:border-slate-400 focus:outline-none focus:ring-0"
						/>
					</div>

					{/* Same tab style as Today's Pending Dispense (All / Partial Delivered). */}
					<div className='flex gap-2 mb-3'>
						{[
							{ value: 'full', label: `Not Delivered (${missedCountFor('full')})` },
							{ value: 'partial', label: `Partially Delivered (${missedCountFor('partial')})` },
						].map((tab) => (
							<button
								key={tab.value}
								type="button"
								className={`p-2 h-10 flex items-center justify-center text-sm font-medium rounded-lg flex-1 ${
									view === tab.value ? 'text-white' : 'bg-gray-200 text-gray-700'
								}`}
								style={view === tab.value ? { backgroundColor: '#FEAE02' } : {}}
								onClick={() => setView(tab.value)}
							>
								{tab.label}
							</button>
						))}
					</div>

					{/* Shown only when there is something to do: a medicine marked
					    missed (to hand over) or one already handed over (to
					    download). Missed medicines must go back to the pharmacy
					    through Route Control > Handover so the on-duty pharmacist can
					    review the missed list. The link opens the Route Control
					    sheet on its Handover tab right here, like the route list. */}
					{(verifiedMissedTotal > 0 || handedOverCount > 0) ? (
					<div className="mb-3 flex items-center gap-2 rounded-md border border-indigo-100 bg-indigo-50/50 px-2.5 py-2">
						<p className="min-w-0 flex-1 text-xs leading-4 text-slate-700">
							<span className="font-semibold text-slate-900">End of day</span>
							{verifiedMissedTotal > 0 ? <span className="font-semibold text-red-700"> ({verifiedMissedTotal} marked)</span> : null}
							<span className="font-semibold text-slate-900">:</span>
							<span className="block">
								1. Tap{' '}
								<button
									type="button"
									onClick={() => setShowHandoverSheet(true)}
									className="font-semibold text-indigo-700 underline underline-offset-2"
								>
									Hand over
								</button>{' '}
								to return the missed medication to the on-duty pharmacist.
							</span>
							<span className="block">
								2. Once they accept it, tap{' '}
								{/* Only medicines already handed over (accepted) go in the list. */}
								<button
									type="button"
									onClick={handleDownloadList}
									disabled={downloading}
									title={handedOverCount === 0 ? 'Hand over is mandatory to download the list' :'Download the handed-over missed list'}
									className={`inline-flex items-center gap-1 font-semibold underline underline-offset-2 ${handedOverCount === 0 ? 'text-slate-400' : 'text-indigo-700'}`}
								>
									{downloading ? (
										<span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-indigo-400 border-t-transparent" />
									) : null}
									{downloading ? 'Preparing…' : 'Download list'}
								</button>{' '}
								to get the missed list.
							</span>
							<span className="mt-1 block font-semibold text-red-700">
								Note: Hand over is mandatory to download the list.
							</span>
						</p>
					</div>
					) : null}

					{verificationError ? (
						<p className="mb-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{verificationError}</p>
					) : null}

					<div className="routes-list">
						{loading ? (
							<div className="flex h-96 items-center justify-center"><LoadingView /></div>
						) : error ? (
							<div className="mb-4 flex h-96 items-center justify-center pb-4"><ErrorMessages error={error} /></div>
						) : routes.length === 0 ? (
							<p className="flex h-96 justify-center pb-4 text-center">You have not picked a route yet.</p>
						) : patients.length === 0 ? (
							<p className="flex h-96 justify-center pb-4">No patients left on your picked routes.</p>
						) : viewPatients.length === 0 ? (
							<p className="flex h-40 justify-center pb-4 text-center">
								{view === 'full' ? 'No single-dose medicine on your routes.' : 'No multi-dose medicine on your routes.'}
							</p>
						) : visiblePatients.length === 0 ? (
							<p className="flex h-40 justify-center pb-4">No patient matches "{searchText.trim()}".</p>
						) : (
							visiblePatients.map((data, index) => {
								return (
									<div key={data.id} className="card bg-white px-4 py-3 rounded-lg shadow-sm mb-3" id={`patientCard-${index}`}>
										{/* The card stays "closed" (no alwaysShowChildren): an open
										    PatientDetails pre-connects a pharmacist video call in the
										    phone app, and this page shows every patient at once. The
										    medications go right under it instead. */}
										<PatientDetails
											data={data}
											patientImg={data.profile_image}
											totalRxData={data.rxList.length}
											index={index}
										/>
										<PickedPatientsRxData
											rxData={data.rxList}
											patientData={data}
											type="new_rx"
											newRxCounsellingValues={[]}
											setNewRxCounsellingValues={() => { }}
											selectedDoses={{}}
											setSelectedDoses={() => { }}
											isActiveDeliverRxCard={false}
											deliverRxSelectionEnabled={false}
											renderRxFooter={(rx) => (
												<VerificationRow
													rx={rx}
													verification={verifications[String(rx?.id)]}
													saving={savingRxIds.includes(String(rx?.id))}
													onSave={handleSaveVerification}
												/>
											)}
										/>
									</div>
								);
							})
						)}
					</div>
				</div>

				{successMessage && <SuccessMessage message={successMessage} setShowToast={() => setSuccessMessage('')} />}
			</div>

			<RouteControlSheet
				isOpen={showHandoverSheet}
				onClose={() => {
					setShowHandoverSheet(false);
					loadHandedOverCount();
				}}
				requestedTab={TAB.HANDOVER}
			/>
		</div>
	);
};

export default PickedPatients;
